import React, { useState, useEffect } from 'react';
import { Trash2, Edit, Eye, Plus, Search, Filter } from 'lucide-react';
import ApiService from '../../services/api';

interface Customer {
  id: number;
  name: string;
  email: string;
  phone: string;
  totalAppointments: number;
  lastAppointmentDate?: string;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

const CustomerManagement: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getCustomers();
      if (response && response.customers) {
        setCustomers(response.customers);
      }
    } catch (error) {
      console.error('Customers load error:', error);
      setMessage({ type: 'error', text: 'Müşteriler yüklenirken hata oluştu' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setShowEditModal(true);
  };

  const handleUpdateCustomer = async (updatedData: Partial<Customer>) => {
    if (!editingCustomer) return;

    try {
      const response = await ApiService.updateCustomer(editingCustomer.id, updatedData);
      if (response && response.success) {
        setMessage({ type: 'success', text: 'Müşteri başarıyla güncellendi' });
        setShowEditModal(false);
        setEditingCustomer(null);
        loadCustomers();
      } else {
        setMessage({ type: 'error', text: response?.message || 'Müşteri güncellenirken hata oluştu' });
      }
    } catch (error) {
      console.error('Customer update error:', error);
      setMessage({ type: 'error', text: 'Müşteri güncellenirken hata oluştu' });
    }
  };

  const handleDeleteCustomer = async (id: number) => {
    if (!window.confirm('Bu müşteriyi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await ApiService.deleteCustomer(id);
      if (response && response.success) {
        setMessage({ type: 'success', text: 'Müşteri başarıyla silindi' });
        loadCustomers();
      } else {
        setMessage({ type: 'error', text: response?.message || 'Müşteri silinirken hata oluştu' });
      }
    } catch (error) {
      console.error('Customer deletion error:', error);
      setMessage({ type: 'error', text: 'Müşteri silinirken hata oluştu' });
    }
  };

  const handleToggleActive = async (customer: Customer) => {
    try {
      const response = await ApiService.updateCustomer(customer.id, {
        isActive: !customer.isActive
      });
      if (response && response.success) {
        setMessage({ type: 'success', text: `Müşteri ${customer.isActive ? 'deaktif' : 'aktif'} edildi` });
        loadCustomers();
      } else {
        setMessage({ type: 'error', text: response?.message || 'Durum güncellenirken hata oluştu' });
      }
    } catch (error) {
      console.error('Customer toggle error:', error);
      setMessage({ type: 'error', text: 'Durum güncellenirken hata oluştu' });
    }
  };

  // Filtreleme ve arama
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.phone.includes(searchTerm);
    
    const matchesFilter = filterActive === 'all' || 
                         (filterActive === 'active' && customer.isActive) ||
                         (filterActive === 'inactive' && !customer.isActive);
    
    return matchesSearch && matchesFilter;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-light tracking-wide">Müşteri Yönetimi</h2>
          <p className="text-gray-400 text-sm">Toplam {customers.length} müşteri</p>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 border rounded ${
          message.type === 'success' 
            ? 'border-green-600 text-green-400 bg-green-900/20' 
            : 'border-red-600 text-red-400 bg-red-900/20'
        }`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Müşteri ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-400 focus:outline-none focus:border-gray-500"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'inactive')}
            className="px-4 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none focus:border-gray-500"
          >
            <option value="all">Tümü</option>
            <option value="active">Aktif</option>
            <option value="inactive">Pasif</option>
          </select>
        </div>
      </div>

      {/* Customers Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-3 px-4 font-medium text-gray-300">Müşteri</th>
              <th className="text-left py-3 px-4 font-medium text-gray-300">İletişim</th>
              <th className="text-left py-3 px-4 font-medium text-gray-300">Randevular</th>
              <th className="text-left py-3 px-4 font-medium text-gray-300">Durum</th>
              <th className="text-left py-3 px-4 font-medium text-gray-300">Kayıt Tarihi</th>
              <th className="text-left py-3 px-4 font-medium text-gray-300">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.map((customer) => (
              <tr key={customer.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                <td className="py-4 px-4">
                  <div>
                    <div className="font-medium text-white">{customer.name}</div>
                    {customer.notes && (
                      <div className="text-sm text-gray-400 mt-1">{customer.notes}</div>
                    )}
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="space-y-1">
                    <div className="text-sm text-gray-300">{customer.email}</div>
                    <div className="text-sm text-gray-400">{customer.phone}</div>
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="space-y-1">
                    <div className="text-sm text-gray-300">
                      Toplam: {customer.totalAppointments}
                    </div>
                    {customer.lastAppointmentDate && (
                      <div className="text-xs text-gray-400">
                        Son: {formatDate(customer.lastAppointmentDate)}
                      </div>
                    )}
                  </div>
                </td>
                <td className="py-4 px-4">
                  <button
                    onClick={() => handleToggleActive(customer)}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      customer.isActive
                        ? 'bg-green-900/20 text-green-400 border border-green-600'
                        : 'bg-red-900/20 text-red-400 border border-red-600'
                    }`}
                  >
                    {customer.isActive ? 'Aktif' : 'Pasif'}
                  </button>
                </td>
                <td className="py-4 px-4">
                  <div className="text-sm text-gray-400">
                    {formatDate(customer.createdAt)}
                  </div>
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditCustomer(customer)}
                      className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded transition-colors"
                      title="Düzenle"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCustomer(customer.id)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors"
                      title="Sil"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {filteredCustomers.length === 0 && (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">👥</div>
          <h3 className="text-lg font-medium text-gray-300 mb-2">
            {searchTerm || filterActive !== 'all' ? 'Müşteri bulunamadı' : 'Henüz müşteri yok'}
          </h3>
          <p className="text-gray-400">
            {searchTerm || filterActive !== 'all' 
              ? 'Arama kriterlerinizi değiştirmeyi deneyin'
              : 'İlk randevu alındığında müşteri burada görünecek'
            }
          </p>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          onClose={() => {
            setShowEditModal(false);
            setEditingCustomer(null);
          }}
          onSave={handleUpdateCustomer}
        />
      )}
    </div>
  );
};

// Edit Customer Modal Component
interface EditCustomerModalProps {
  customer: Customer;
  onClose: () => void;
  onSave: (data: Partial<Customer>) => void;
}

const EditCustomerModal: React.FC<EditCustomerModalProps> = ({ customer, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: customer.name,
    email: customer.email,
    phone: customer.phone,
    notes: customer.notes || '',
    isActive: customer.isActive
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-md">
        <h3 className="text-xl font-medium mb-4">Müşteri Düzenle</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">İsim</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none focus:border-gray-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">E-posta</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none focus:border-gray-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Telefon</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none focus:border-gray-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Notlar</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none focus:border-gray-500"
            />
          </div>
          
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="isActive" className="text-sm">Aktif</label>
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Kaydet
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
            >
              İptal
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerManagement;
